import { memo, useMemo } from 'react';
import LoadingBox from '../../components/LoadingBox';
import { OPERATION_TYPE, VESSEL_TYPE, vesselTypes } from '../../util/varibles/constants';
import { FleetUtilizationItem, IVessel } from '../../util/varibles/interface';
import FleetInsight from './FleetInsight';
import FleetUtilizationChart, { REMAINING_COLOR } from './FleetUtilizationChart';
import useServiceTasks from './hooks/useServiceTasks';
import { getRemainingPercentage } from './percentage';
import styles from './style.module.scss';
import { useVesselOverview } from './VesselOverviewContext';

interface IProps {
    data: FleetUtilizationItem[];
    vessels: IVessel[];
    countVessels: IVessel[];
    filteredCount: number;
}

const COLORS = {
    operation: '#9fd4c4',
    hygine: '#ef7f83',
    maintenance: '#f1ad66',
    idle: '#b7b4aa',
    travelling: '#57a4d8',
};

const METRICS = [
    { key: 'operation', label: 'Avg. Operation', color: COLORS.operation },
    { key: 'hygine', label: 'Avg. Quarantine, Hygiene', color: COLORS.hygine },
    { key: 'maintenance', label: 'Avg. Maintenance', color: COLORS.maintenance },
    { key: 'idle', label: 'Avg. Idle', color: COLORS.idle },
    { key: 'travelling', label: 'Avg. Traveling', color: COLORS.travelling },
] as const;

const OPERATION_LABELS: Record<string, string> = {
    [OPERATION_TYPE.HARVEST]: 'Harvest',
    [OPERATION_TYPE.TREATMENT]: 'Treatment',
    [OPERATION_TYPE.TRANSPORT]: 'Smolt',
    [OPERATION_TYPE.SERVICE]: 'Service',
    [OPERATION_TYPE.EVENT]: 'Other',
};

const WELLBOAT_OPERATION_TYPES = [
    OPERATION_TYPE.TREATMENT,
    OPERATION_TYPE.HARVEST,
    OPERATION_TYPE.TRANSPORT,
    OPERATION_TYPE.SERVICE,
    OPERATION_TYPE.EVENT,
];

const formatPercent = (value?: number) => {
    if (value == null || !Number.isFinite(value)) return 'xx%';

    return `${Number.isInteger(value) ? value : value.toFixed(2)}%`;
};

const DistributionRow = ({ label, value }: { label: string; value?: number }) => (
    <div className={styles['distribution-row']}>
        <span className={styles['distribution-label']}>{label}</span>
        <span className={styles.track}>
            <span
                className={styles.bar}
                style={{
                    width: `${Math.min(100, Math.max(0, value || 0))}%`,
                    minWidth: value != null && value > 0 ? 8 : 0,
                }}
            />
        </span>
        <span>{formatPercent(value)}</span>
    </div>
);

function Sumary({ data, vessels, countVessels, filteredCount }: IProps) {
    const serviceTasks = useServiceTasks(vessels);
    const [duration] = useVesselOverview(state => state.duration);
    const [loadingVesselIds] = useVesselOverview(state => state.loadingVesselIds);
    const [vesselTypeFilter, setStore] = useVesselOverview(state => state.vesselTypeFilter);
    const loadingSummary = loadingVesselIds.length > 0;
    const durationTime = duration[1] - duration[0];
    const averages = useMemo(() => {
        if (!data.length || durationTime <= 0) return undefined;

        const totals = data.reduce<number[]>((result, item) => {
            METRICS.forEach(({ key }, index) => {
                result[index] += Number(item[key] || 0);
            });
            return result;
        }, METRICS.map(() => 0));

        return totals.map(total => total / data.length / durationTime * 100);
    }, [data, durationTime]);
    const chartValues = useMemo(
        () => averages?.map(value => value || 0) || METRICS.map(() => 0),
        [averages],
    );
    const noDataPercentage = useMemo(
        () => getRemainingPercentage(chartValues),
        [chartValues],
    );

    const vesselCounts = useMemo(() => countVessels.reduce<Record<string, number>>((result, vessel) => {
        result[vessel.type] = (result[vessel.type] || 0) + 1;
        return result;
    }, {}), [countVessels]);
    const toggleVesselType = (type: VESSEL_TYPE) => {
        setStore({
            vesselTypeFilter: vesselTypeFilter.includes(type)
                ? vesselTypeFilter.filter(item => item !== type)
                : [...vesselTypeFilter, type],
        });
    };
    const operationTotals = useMemo(() => {
        const vesselTypesById = new Map(vessels.map(vessel => [vessel.id, vessel.type]));
        const ignoredKeys = new Set(['vessel_id', ...METRICS.map(item => item.key)]);
        const totals: Record<string, Record<string, number>> = {
            [VESSEL_TYPE.SERVICE]: {},
            [VESSEL_TYPE.WELL_BOAT]: {},
        };

        data.forEach(item => {
            const type = vesselTypesById.get(item.vessel_id);
            const typeTotals = type == null ? undefined : totals[type];
            if (!typeTotals) return;

            Object.entries(item).forEach(([key, value]) => {
                if (!ignoredKeys.has(key) && typeof value === 'number') {
                    typeTotals[key] = (typeTotals[key] || 0) + value;
                }
            });
        });

        return totals;
    }, [data, vessels]);
    const serviceOperationTotals = operationTotals[VESSEL_TYPE.SERVICE];
    const wellboatOperationTotals = operationTotals[VESSEL_TYPE.WELL_BOAT];
    const wellboatServiceDuration = Object.entries(wellboatOperationTotals)
        .filter(([key]) =>
            key === String(OPERATION_TYPE.SERVICE)
            || key.startsWith(`${OPERATION_TYPE.SERVICE}_`)
        )
        .reduce((total, [, value]) => total + value, 0);
    const getWellboatOperationDuration = (type: OPERATION_TYPE) =>
        type === OPERATION_TYPE.SERVICE
            ? wellboatServiceDuration
            : wellboatOperationTotals[String(type)] || 0;
    const wellboatTotalDuration = WELLBOAT_OPERATION_TYPES.reduce(
        (total, type) => total + getWellboatOperationDuration(type),
        0,
    );
    const wellboatRows = WELLBOAT_OPERATION_TYPES
        .filter(type =>
            type !== OPERATION_TYPE.SERVICE
            || wellboatServiceDuration > 0
        )
        .map(type => ({
            label: OPERATION_LABELS[type],
            value: wellboatTotalDuration > 0
                ? getWellboatOperationDuration(type) / wellboatTotalDuration * 100
                : 0,
        }));
    const serviceOtherDuration = serviceOperationTotals[String(OPERATION_TYPE.EVENT)] || 0;
    const serviceTotalDuration = serviceTasks.reduce(
        (total, task) =>
            total + (serviceOperationTotals[`${OPERATION_TYPE.SERVICE}_${task.id}`] || 0),
        serviceOtherDuration,
    );
    const serviceTaskRows = serviceTasks
        .filter(task => Object.prototype.hasOwnProperty.call(
            serviceOperationTotals,
            `${OPERATION_TYPE.SERVICE}_${task.id}`,
        ))
        .map(task => {
            const taskDuration =
                serviceOperationTotals[`${OPERATION_TYPE.SERVICE}_${task.id}`] || 0;

            return {
                label: task.name,
                value: serviceTotalDuration > 0
                    ? taskDuration / serviceTotalDuration * 100
                    : 0,
            };
        });
    const serviceRows = serviceOtherDuration > 0
        ? [...serviceTaskRows, {
            label: OPERATION_LABELS[OPERATION_TYPE.EVENT],
            value: serviceOtherDuration / serviceTotalDuration * 100,
        }]
        : serviceTaskRows;

    return <aside className={styles.summary}>
        <FleetUtilizationChart
            metrics={METRICS}
            values={chartValues}
        />

        <div className={styles.legend}>
            {METRICS.map((metric, index) => <div key={metric.key} className={styles['legend-item']}>
                <span className={styles.dot} style={{ backgroundColor: metric.color }} />
                <span>{metric.label} ({formatPercent(averages?.[index])})</span>
            </div>)}
            <div className={styles['legend-item']}>
                <span className={styles.dot} style={{ backgroundColor: REMAINING_COLOR }} />
                <span>No data ({formatPercent(noDataPercentage)})</span>
            </div>
        </div>

        <div className={styles.chips}>
            {vesselTypes.map(type => {
                const count = vesselCounts[type.id];
                if (!count) return null;

                const active = vesselTypeFilter.includes(type.id);
                return <button
                    key={type.id}
                    type="button"
                    className={active ? styles.active : ''}
                    aria-pressed={active}
                    onClick={() => toggleVesselType(type.id)}
                >
                    {count} {type.name}
                </button>;
            })}
        </div>

        <FleetInsight filteredCount={filteredCount} />

        <section className={styles.distribution}>
            <div className='text-[15px] font-[600]'>Wellboat operation distribution</div>
            {wellboatRows.length
                ? wellboatRows.map(item => <DistributionRow key={item.label} {...item} />)
                : <LoadingBox />}
        </section>
        <section className={styles.distribution}>
            <div className='text-[15px] font-[600]'>Service Vessel operation distribution</div>
            {serviceRows.length
                ? serviceRows.map(item => <DistributionRow key={item.label} {...item} />)
                : <LoadingBox className='mt-2' loading={loadingSummary} />}
        </section>
    </aside>;
}

export default memo(Sumary);
